var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var OrthographicFixedCamera_1;
import { IVector2, IVector3, IMatrix4, CardinalDirection, LineType, ITransformedBoundingBox } from '../../api.js';
import { clamp } from '../../Math.js';
import { OrthographicProjection } from '../OrthographicProjection.js';
import { ViewerCamera } from '../../babylon/camera/ViewerCamera.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
import { ORTHOGRAPHIC_CAMERA_DESCRIPTION } from './debug/OrthographicCameraDescription.js';
import { OrthographicCameraVisualizer } from './debug/visualizers/OrthographicCameraVisualizer.js';
import { create_visualizers_container } from './debug/visualizers/VisualizersContainer.js';
import { CameraFrustumVisualizer } from '../../babylon/debug/visualizers/CameraFrustumVisualizer.js';
const DEFAULT_FRUSTUM_VALUE = 2.5;
const MIN_FRUSTUM_VALUE = 0.005; // Minimum height is then 1 centimeter
let OrthographicFixedCamera = OrthographicFixedCamera_1 = class OrthographicFixedCamera extends ViewerCamera {
    constructor(position_on_plane, viewer) {
        super(viewer);
        this._position_on_plane = new IVector2(0, 0); // Target position in the screen plane (right and up)
        this._is_dirty = true;
        this._aspect_ratio = 1;
        this._scene_bounds_changed_observer = null;
        this._right = IVector3.copy(ViewerCamera.RIGHT);
        this._up = IVector3.copy(ViewerCamera.UP);
        this.name = 'OrthographicFixedCamera';
        this._debug_geometry = null;
        // When a new object has been added or an object moved, we want to check if the object is behind the camera.
        // In that case, we want to move the camera "back" so that the object will be visible.
        this.on_scene_bounds_changed = (_, scene_bounds) => {
            this._scene_bounds = scene_bounds;
            this.is_dirty = true;
        };
        this._camera_transform_cache = new IMatrix4();
        this._view_matrix_cache = new IMatrix4();
        this._projection = new OrthographicProjection(-DEFAULT_FRUSTUM_VALUE * this._aspect_ratio, DEFAULT_FRUSTUM_VALUE * this._aspect_ratio, -DEFAULT_FRUSTUM_VALUE, DEFAULT_FRUSTUM_VALUE, 0.01, 100);
        this._scene_bounds = viewer.get_scene_bounds();
        this.set_direction(CardinalDirection.FRONT);
        this.position_on_plane = position_on_plane;
    }
    update_aspect_ratio(aspect_ratio) {
        if (this._aspect_ratio === aspect_ratio)
            return;
        this._aspect_ratio = aspect_ratio;
        this.projection_left = -this._projection.top * this._aspect_ratio;
        this.projection_right = this._projection.top * this._aspect_ratio;
        this.is_dirty = true;
    }
    get_projection_matrix(_custom_aspect_ratio) {
        if (_custom_aspect_ratio === undefined) {
            return this.projection.get_projection_matrix();
        }
        else {
            const projection = this.projection.clone();
            projection.left = -this.projection.top * _custom_aspect_ratio;
            projection.right = this.projection.top * _custom_aspect_ratio;
            return projection.get_projection_matrix();
        }
    }
    set projection_left(val) {
        if (this._projection.left !== val) {
            this._projection.left = val;
            this.is_dirty = true;
        }
    }
    set projection_right(val) {
        if (this._projection.right !== val) {
            this._projection.right = val;
            this.is_dirty = true;
        }
    }
    set projection_top(val) {
        if (this._projection.top !== val) {
            this._projection.top = val;
            this.is_dirty = true;
        }
    }
    set projection_bottom(val) {
        if (this._projection.bottom !== val) {
            this._projection.bottom = val;
            this.is_dirty = true;
        }
    }
    set position_on_plane(val) {
        this._position_on_plane.x = val.x;
        this._position_on_plane.y = val.y;
        this.is_dirty = true;
    }
    get position_on_plane() {
        return this._position_on_plane;
    }
    /**
     * Sets the size of the orthographic view in world units (meters)
     * Sets the height of the view. The width will be scaled with the aspect ratio.
     * The size must be larger than zero
     */
    set size(val) {
        const size = clamp(val, MIN_FRUSTUM_VALUE, Number.POSITIVE_INFINITY);
        this.projection_left = -size * 0.5 * this._aspect_ratio;
        this.projection_right = size * 0.5 * this._aspect_ratio;
        this.projection_top = size * 0.5;
        this.projection_bottom = -size * 0.5;
    }
    /**
     * Set the direction in which the camera should look.
     * @param direction The Orthographic cameras direction. Can be be either CardinalDirection.TOP, FRONT, BACK, LEFT or RIGHT (Not BOTTOM)
     */
    set_direction(direction) {
        switch (direction) {
            case CardinalDirection.TOP:
                this.set_view_plane(ViewerCamera.RIGHT, ViewerCamera.FORWARD);
                break;
            case CardinalDirection.FRONT:
                this.set_view_plane(ViewerCamera.RIGHT, ViewerCamera.UP);
                break;
            case CardinalDirection.BACK:
                this.set_view_plane(IVector3.scale(ViewerCamera.RIGHT, -1), ViewerCamera.UP);
                break;
            case CardinalDirection.LEFT:
                this.set_view_plane(IVector3.scale(ViewerCamera.FORWARD, -1), ViewerCamera.UP);
                break;
            case CardinalDirection.RIGHT:
                this.set_view_plane(ViewerCamera.FORWARD, ViewerCamera.UP);
                break;
            case CardinalDirection.BOTTOM:
                this.set_view_plane(ViewerCamera.RIGHT, IVector3.scale(ViewerCamera.FORWARD, -1));
                break;
        }
    }
    set_view_plane(right, up) {
        IVector3.copy_to_ref(IVector3.normalize_safe(right), this._right);
        IVector3.copy_to_ref(IVector3.normalize_safe(up), this._up);
    }
    center_camera_on_position(center_position) {
        this.position_on_plane = {
            x: IVector3.dot(this._right, center_position),
            y: IVector3.dot(this._up, center_position)
        };
    }
    get projection() {
        return this._projection;
    }
    get blend_target() {
        // This should be the point in space where the look vector intersects the plane with -look as normal intersecting the would bounds center
        const scene_bounds = this.viewer.get_scene_bounds();
        const bounds_center = IVector3.scale_in_place(IVector3.add(scene_bounds.min, scene_bounds.max), 0.5);
        const distance = IVector3.dot(IVector3.subtract(bounds_center, this.position), this.forward);
        return IVector3.add(this.position, IVector3.scale(this.forward, distance));
    }
    /**
     * Flag indicating if any of the prameters have changed since last generation of view matrix
     */
    get is_dirty() {
        return this._is_dirty;
    }
    set is_dirty(val) {
        this._is_dirty = val;
    }
    /**
     * Called whenever InputManager decided that the camera is the receiver of a Drag action
     * @param evt Event with the drag info
     */
    drag_input(evt) {
        // Drag input is not supported for Static Orthographic cameras
    }
    pan_input(evt) {
        // Pan input is not supported for Static Orthographic cameras
    }
    /**
     * Called whenever the InputManager decided that the camera is the receiver of a zoom action (scroll or pinch)
     * @param evt Event with the zoom info
     */
    zoom_input(evt) {
        // Zoom input is not supported for Static Orthographic cameras
    }
    get camera_transform() {
        this.update_camera_transform_cache();
        return this._camera_transform_cache;
    }
    get view_matrix() {
        this.update_camera_transform_cache();
        return this._view_matrix_cache;
    }
    update_camera_transform_cache() {
        if (!this.is_dirty)
            return;
        IVector3.copy_to_ref(this._right, this._camera_transform_cache[0]);
        IVector3.copy_to_ref(this._up, this._camera_transform_cache[1]);
        IVector3.cross_to_ref(this._up, this._right, this._camera_transform_cache[2]);
        IVector3.set(this._camera_transform_cache[3], 0, 0, 0);
        IMatrix4.copy_to_ref(IMatrix4.transpose(this._camera_transform_cache), this._view_matrix_cache);
        const bounds_in_camera_space = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(new ITransformedBoundingBox(this._scene_bounds, this._view_matrix_cache));
        // Add some margin to our bounds to give room for numerical errors. Arbitrarily chosen value.
        const bounds_depth_epsilon = 0.1;
        bounds_in_camera_space.min.z -= bounds_depth_epsilon;
        bounds_in_camera_space.max.z += bounds_depth_epsilon;
        this._projection.near = 0.0;
        this._projection.far = bounds_in_camera_space.max.z - bounds_in_camera_space.min.z;
        IVector3.set(this._view_matrix_cache[3], -this._position_on_plane.x, -this._position_on_plane.y, -bounds_in_camera_space.min.z);
        IMatrix4.orthonormal_rotation_affine_transform_inverse_to_ref(this._view_matrix_cache, this._camera_transform_cache);
        this.is_dirty = false;
    }
    on_activation() {
        if (!this._scene_bounds_changed_observer) {
            this._scene_bounds_changed_observer = this.viewer.get_scene_bounds_changed_observable().add(this.on_scene_bounds_changed);
        }
        // The scene bounds might have changed since we created the camera
        this._scene_bounds = this.viewer.get_scene_bounds();
        this.is_dirty = true;
    }
    on_deactivation() {
        if (this._scene_bounds_changed_observer) {
            this.viewer.get_scene_bounds_changed_observable().remove(this._scene_bounds_changed_observer);
            this._scene_bounds_changed_observer = null;
        }
    }
    update(_) {
        var _a;
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.update();
    }
    get debug_geometry() {
        return this._debug_geometry;
    }
    create_debug_geometry() {
        if (this._debug_geometry)
            return;
        this._debug_geometry = create_visualizers_container({
            frustum: new CameraFrustumVisualizer(this, this.viewer, undefined, 0.01, LineType.DASHED),
            camera: new OrthographicCameraVisualizer(this, this.viewer)
        }, this.viewer, true);
    }
    remove_debug_geometry() {
        var _a;
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.dispose();
        this._debug_geometry = null;
    }
    update_debug_geometry() {
        if (this.debug_geometry) {
            this.debug_geometry.update();
        }
    }
    /**
     * Frames the bounds specified as snuggly as possible.
     * @param bounds The bounds that you want to frame
     */
    frame_precise(bounds, aspect_ratio) {
        const center = IVector3.scale_in_place(IVector3.add(bounds.min, bounds.max), 0.5);
        const position = IVector3.subtract(center, this.forward);
        const camera_matrix = IMatrix4.look_at(position, center, this._up);
        const view_matrix = IMatrix4.orthonormal_rotation_affine_transform_inverse(camera_matrix);
        const bounds_in_camera_space = ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(new ITransformedBoundingBox(this._scene_bounds, view_matrix));
        const max_abs_x = Math.abs(bounds_in_camera_space.max.x);
        const max_abs_y = Math.abs(bounds_in_camera_space.max.y);
        const offset_x = IVector3.multiply(this._right, center);
        const offset_y = IVector3.multiply(this._up, center);
        this.position_on_plane = { x: offset_x.x + offset_x.y + offset_x.z, y: offset_y.x + offset_y.y + offset_y.z };
        const max = max_abs_x > max_abs_y ? max_abs_x : max_abs_y;
        if (aspect_ratio === undefined) {
            aspect_ratio = this._aspect_ratio;
        }
        this.projection_left = aspect_ratio >= 1 ? -max * aspect_ratio : -max;
        this.projection_right = aspect_ratio >= 1 ? max * aspect_ratio : max;
        this.projection_top = aspect_ratio >= 1 ? max : max / aspect_ratio;
        this.projection_bottom = aspect_ratio >= 1 ? -max : -max / aspect_ratio;
    }
    /**
     * Clones the camera to create an exact duplicate.
     * @returns A new Camera instance that is an exact copy of the original camera.
     */
    clone() {
        const { x, y } = this._position_on_plane;
        const clone = new OrthographicFixedCamera_1({ x: x, y: y }, this.viewer);
        clone._projection = this._projection.clone();
        clone.set_view_plane(this.right, this.up);
        return clone;
    }
    dispose_impl() {
        if (this._scene_bounds_changed_observer) {
            this.viewer.get_scene_bounds_changed_observable().remove(this._scene_bounds_changed_observer);
            this._scene_bounds_changed_observer = null;
        }
    }
};
OrthographicFixedCamera = OrthographicFixedCamera_1 = __decorate([
    Debug.Describe(Object.assign(Object.assign({}, ORTHOGRAPHIC_CAMERA_DESCRIPTION), { name: 'orthographic-fixed-camera-description' }))
], OrthographicFixedCamera);
export { OrthographicFixedCamera };
//# sourceMappingURL=OrthographicFixedCamera.js.map